/**
 * Ce que chaque bloc et chaque champ font réellement.
 *
 * Le texte vit ici plutôt que dans les écrans, pour une raison pratique : ces
 * explications se relisent, se corrigent au fil des questions du support, et
 * les chercher au milieu du balisage de six écrans découragerait de les
 * améliorer.
 *
 * Elles s'écrivent pour quelqu'un qui découvre le logiciel, pas pour qui l'a
 * construit. Trois questions à chaque fois : à quoi ça sert, que se passe-t-il
 * si je n'y touche pas, et qu'est-ce que ça change une fois rempli. Un texte
 * qui se contente de répéter le libellé du champ ne vaut pas la place qu'il
 * prend — mieux vaut alors ne rien mettre.
 */

/* ----------------------------- Les blocs ------------------------------- */

export const AIDES_BLOCS: Record<string, React.ReactNode> = {
  'Qui tient quelle fonction': (
    <>
      <p>
        Le bureau au complet, chacun avec sa fonction. La fonction n'est pas un titre honorifique :
        c'est elle qui porte les droits, et changer la ligne d'un membre change ce qu'il voit et ce
        qu'il peut faire dès sa prochaine connexion.
      </p>
      <p>
        Après une assemblée générale, il fallait rouvrir huit fiches l'une après l'autre — sans
        jamais voir l'ensemble pour vérifier qu'il ne manquait personne, ni qu'une fonction n'était
        restée sur l'ancien titulaire. Les deux se signalent ici.
      </p>
      <p>
        Un membre sans fonction n'est pas privé d'accès : il garde les droits de base du bureau.
        Faire d'un oubli administratif une porte fermée un soir de fête serait le pire des zèles.
      </p>
    </>
  ),

  'Exporter mes données': (
    <>
      <p>
        Tout ce que votre espace contient, au format CSV, plus les documents déposés en archive.
        Un fichier par nature : membres, bénévoles, adhérents, sponsors, factures, achats.
      </p>
      <p>
        L'export reste disponible <strong>même si l'abonnement est suspendu</strong>. Ces données
        sont les vôtres, et un logiciel qui les retiendrait pour se faire payer ne vaudrait pas
        mieux que le tableur qu'il remplace.
      </p>
    </>
  ),

  'Importer des données': (
    <>
      <p>
        Reprendre un fichier existant plutôt que tout ressaisir. Le modèle à remplir se télécharge
        ici même, colonne par colonne.
      </p>
      <p>
        Chaque import est contrôlé avant d'être appliqué et vous montre ce qu'il s'apprête à
        écrire. Un fichier qui se déverse sans prévenir est le meilleur moyen de créer trois cents
        doublons qu'il faudra reprendre un par un.
      </p>
    </>
  ),

  Personnalisation: (
    <>
      <p>
        L'apparence de votre espace et de votre site public : <strong>logo</strong>, couleurs,
        image d'accueil, et les cartes visibles depuis la page d'accueil.
      </p>
      <p>
        Le logo sert partout — en-tête du site, favicon de l'onglet, icône sur un téléphone qui
        ajoute le site à son écran d'accueil, et en tête de vos devis et factures. Un carré d'au
        moins 512 pixels donne le meilleur rendu.
      </p>
    </>
  ),

  'Rôles et statuts': (
    <>
      <p>
        Qui peut faire quoi. Chaque membre reçoit un ou plusieurs rôles, et chaque rôle ouvre des
        écrans : le trésorier voit la facturation, le secrétaire les réunions, un bénévole
        seulement son planning.
      </p>
      <p>
        Un membre sans rôle voit le site comme un visiteur. C'est le réglage à revoir après une
        assemblée générale, quand le bureau change : <strong>retirer un rôle est aussi important
        que d'en donner un</strong>.
      </p>
    </>
  ),

  'Encaissement en ligne': (
    <>
      <p>
        Les clés du compte qui encaisse vos règlements en ligne : cotisations, billetterie,
        boutique, dons et factures de sponsoring.
      </p>
      <p>
        Ce compte est celui de votre association, ouvert à son nom et alimentant son IBAN. L'éditeur
        n'y a aucun accès et ne prélève aucune commission. Tant que les clés ne sont pas
        renseignées, <strong>le paiement en ligne reste fermé</strong> et vos adhérents règlent au
        bureau.
      </p>
    </>
  ),

  'Tailles de vêtements': (
    <>
      <p>
        Les tailles proposées quand vous prêtez ou vendez un équipement : maillots, survêtements,
        sacs. Chaque type d'équipement a les siennes — un short se décline autrement qu'une paire
        de chaussettes.
      </p>
      <p>
        Elles servent au registre des adhérents, pas à la boutique : celle-ci porte ses propres
        tailles, article par article.
      </p>
    </>
  ),

  Modules: (
    <>
      <p>
        Ce que votre abonnement comprend, et ce qui peut s'y ajouter. Chaque module ouvre un
        ensemble d'écrans : la billetterie, le registre des adhérents, le coffre-fort, le suivi des
        achats.
      </p>
      <p>
        Un module dont vous ne vous servez pas se met sur « Non » : il quitte votre espace{' '}
        <strong>et vos prochaines échéances</strong>. Ce que vous avez déjà réglé pour la période
        en cours reste utilisable jusqu'à son terme.
      </p>
    </>
  ),

  'Réseaux sociaux': (
    <>
      <p>
        Les comptes de l'association, affichés en pied de votre site public et sur vos affiches
        d'événement.
      </p>
      <p>
        Laissez vide ce que vous n'avez pas : un lien vers une page inexistante fait plus de tort
        que son absence.
      </p>
    </>
  ),

  'Mairie & CC': (
    <>
      <p>
        Votre mairie et votre communauté de communes, avec leurs logos. Ce sont vos partenaires
        institutionnels : ils apparaissent sur le site public et sur les documents où leur soutien
        doit être visible.
      </p>
      <p>
        Beaucoup de subventions imposent d'afficher le logo du financeur. C'est ici que cette
        obligation se règle une fois pour toutes.
      </p>
    </>
  ),

  'Mise en production': (
    <>
      <p>
        Le passage de la démonstration à l'usage réel. Tant que votre espace est en démonstration,
        tous les modules sont ouverts et garnis d'exemples, pour que vous puissiez tout parcourir.
      </p>
      <p>
        <strong>Le passage en production efface les données d'exemple</strong> — factures, dons et
        réservations fictifs — pour que votre première année réelle parte d'une caisse vide. Vos
        réglages, vos comptes et ce que vous avez créé vous-même sont conservés.
      </p>
    </>
  ),

  'Coffre-fort sécurisé': (
    <>
      <p>
        Les comptes de l'association — identifiants, mots de passe, codes à six chiffres — chiffrés
        et partagés avec le seul bureau.
      </p>
      <p>
        Il remplace le carnet, le fichier partagé et les mots de passe envoyés par SMS. Au
        changement de bureau, les accès se transmettent sans qu'on ait à les redemander un par un —
        ni à espérer que l'ancien trésorier réponde encore.
      </p>
    </>
  ),

  'Bot Telegram': (
    <>
      <p>
        Deux canaux animés par le robot de l'association : l'un réservé au bureau, l'autre ouvert
        aux adhérents.
      </p>
      <p>
        Le canal du bureau reçoit ce qui demande une décision ou touche à l'argent ; le canal
        général, ce qui se partage. C'est vous qui décidez qui accède à l'un et à l'autre.
      </p>
    </>
  ),

  /* ---------------- Les groupes de paramètres généraux ---------------- */

  Messagerie: (
    <>
      <p>
        Les adresses d'où partent vos courriers et celles qui les reçoivent : confirmations
        d'inscription, relances de cotisation, factures aux sponsors.
      </p>
      <p>
        L'adresse d'expédition doit appartenir au domaine de votre espace. Un message envoyé depuis
        une adresse extérieure — un compte personnel, par exemple — <strong>finit en
        indésirables</strong> chez la moitié de vos destinataires, sans que rien ne vous le signale.
      </p>
    </>
  ),

  Facturation: (
    <>
      <p>
        Les mentions qui figurent sur vos devis et factures : numérotation, conditions de
        règlement, texte de pied de page.
      </p>
      <p>
        La numérotation doit être continue et sans trou — c'est une obligation comptable. Le
        logiciel s'en charge à partir du dernier numéro émis : n'y touchez qu'en connaissance de
        cause.
      </p>
    </>
  ),

  'Mentions légales': (
    <>
      <p>
        L'identité de votre association telle qu'elle paraît sur votre site public et sur vos
        documents : numéro RNA, parution au Journal officiel, siège, objet.
      </p>
      <p>
        Ces mentions sont obligatoires pour un site accessible au public. Ce que vous n'avez pas —
        certaines associations n'ont ni SIRET ni parution — se déclare « laissé vierge » plutôt que
        d'être inventé.
      </p>
    </>
  ),

  'Coordonnées bancaires': (
    <>
      <p>
        L'IBAN de l'association, son titulaire, et l'ordre auquel les chèques doivent être libellés.
      </p>
      <p>
        Ils apparaissent sur les factures réglées par virement et sur les pages de règlement. Une
        erreur ici envoie l'argent ailleurs : <strong>relisez-les à voix haute</strong>, caractère
        par caractère, avant d'enregistrer.
      </p>
    </>
  ),

  'Boutique (option)': (
    <>
      <p>
        Les réglages de votre boutique de goodies : le texte affiché après une commande, et la
        grille des frais de port.
      </p>
      <p>
        Les frais suivent le colis : chaque article déclare ce qu'il y pèse, et la grille donne le
        prix selon le total. Un porte-clefs seul ne doit pas coûter l'envoi d'un carton de
        maillots.
      </p>
    </>
  ),

  Général: (
    <>
      <p>
        Les réglages qui ne relèvent d'aucune autre section : nom de l'association, fuseau, clôture
        de l'exercice comptable.
      </p>
    </>
  ),
};

/* ---------------------- Les champs qui le méritent ---------------------- */

/**
 * Seuls les champs dont le libellé ne suffit pas.
 *
 * « Nom de l'association » n'a besoin de rien. « Clôture automatique » ou
 * « Adresse d'expédition », si : ce qu'ils font n'est pas dans leur nom, et
 * s'en apercevoir coûte cher.
 */
export const AIDES_CHAMPS: Record<string, React.ReactNode> = {
  EMAIL_EXPEDITEUR: (
    <p>
      L'adresse d'où partent vos courriers. Elle doit appartenir au domaine de votre espace —
      <code className="mx-1">contact@votre-association.gest-asso.com</code> par exemple. Une adresse
      extérieure, même valide, fait échouer les contrôles d'authenticité des messageries : vos
      courriers arrivent en indésirables, ou n'arrivent pas.
    </p>
  ),

  EMAIL_BUREAU: (
    <p>
      L'adresse qui <strong>reçoit</strong> : demandes de sponsoring, questions des adhérents,
      avis de règlement. C'est aussi celle vers laquelle répondent vos destinataires. Une boîte que
      personne ne relève est le meilleur moyen de perdre un partenaire.
    </p>
  ),

  CLOTURE_AUTOMATIQUE: (
    <>
      <p>
        Quand votre exercice comptable se ferme et qu'un nouveau s'ouvre. Une fois clos, un
        exercice n'accepte plus d'écriture : ses chiffres sont figés pour l'assemblée générale.
      </p>
      <p>
        <strong>Annuelle</strong> au 31 décembre convient à la plupart des associations.{' '}
        <strong>Calendaire</strong> ferme douze mois après votre déclaration — utile à un club dont
        la saison court de septembre à août.
      </p>
    </>
  ),

  PAIEMENT_IBAN: (
    <p>
      L'IBAN sur lequel vos adhérents et sponsors virent leur règlement. Il paraît sur les factures
      et les pages de paiement. Vérifiez-le caractère par caractère : un IBAN faux mais bien formé
      est accepté sans broncher, et l'argent part chez quelqu'un d'autre.
    </p>
  ),

  PAIEMENT_ADRESSE_CHEQUE: (
    <p>
      L'ordre auquel les chèques doivent être libellés, et l'adresse où les envoyer. C'est le texte
      exact que lira le donateur ou le sponsor : écrivez-le comme il doit apparaître sur le chèque,
      sans abréviation.
    </p>
  ),

  STRIPE_PUBLIC_KEY: (
    <p>
      La clé publique de votre compte d'encaissement. Elle commence par{' '}
      <code>pk_test_</code> pour les essais, <code>pk_live_</code> pour les paiements réels. Elle
      n'est pas secrète : elle circule dans la page de paiement.
    </p>
  ),

  STRIPE_SECRET_KEY: (
    <p>
      La clé secrète, qui autorise à encaisser. Elle commence par <code>sk_test_</code> ou{' '}
      <code>sk_live_</code>. <strong>Elle ne s'affiche qu'une fois</strong> chez votre prestataire :
      si vous la perdez, il faut en régénérer une nouvelle, et l'ancienne cesse de fonctionner.
    </p>
  ),

  STRIPE_WEBHOOK_SECRET: (
    <>
      <p>
        Ce qui permet à votre espace de vérifier que les avis de paiement viennent bien de votre
        prestataire, et non de quelqu'un qui aurait deviné son adresse.
      </p>
      <p>
        <strong>C'est le réglage dont l'oubli ne se voit pas.</strong> Sans lui, les paiements sont
        encaissés mais votre espace n'en est jamais informé : les cotisations restent marquées
        impayées et vous relancez des familles qui ont déjà payé.
      </p>
    </>
  ),

  BOUTIQUE_INSTRUCTIONS: (
    <p>
      Le texte affiché à l'acheteur après sa commande, et repris dans son courriel de confirmation.
      Dites-y ce qui va se passer : sous quel délai le colis part, et à qui écrire en cas de
      question.
    </p>
  ),

  PAIEMENT_LIEN_EXTERNE_URL: (
    <>
      <p>
        L'adresse d'une page de règlement que vous tenez vous-même : une cagnotte, un lien fourni
        par votre banque. Elle s'affiche au payeur à côté du virement et du
        chèque. Laissez vide si vous n'en avez pas.
      </p>
      <p>
        <strong>Rien ne revient de ce lien.</strong> Le logiciel ne saura pas qu'il a été payé : la
        relance repartira, le reçu ne partira pas, et c'est au trésorier de reporter la somme depuis
        l'écran Encaissements. Le montant n'est pas garanti non plus — un lien libre laisse taper ce
        qu'on veut.
      </p>
      <p>
        C'est pour cela qu'il vaut mieux, quand c'est possible, encaisser par carte : le règlement
        est confirmé en quelques secondes et la pièce se solde toute seule.
      </p>
    </>
  ),

  PAIEMENT_LIEN_EXTERNE_LIBELLE: (
    <p>
      Ce que le payeur lit sur le bouton : « Notre cagnotte », « Payer en ligne ». Nommez
      l'outil qu'il va découvrir en cliquant — un bouton qui ne dit pas où il mène ne se clique pas.
    </p>
  ),

  PAIEMENT_LIEN_EXTERNE_AIDE: (
    <p>
      Ce qu'il faut dire avant le clic : le montant à saisir si le lien ne le fixe pas, la référence
      à indiquer pour que vous retrouviez le règlement. Sans référence, une somme arrive sans nom et
      le rapprochement se fait à la mémoire.
    </p>
  ),

  GRATUITE_FIN: (
    <p>
      La date jusqu'à laquelle votre espace vous est offert. Elle est posée par l'éditeur et ne se
      modifie pas ici. Jusque-là, tous les modules sont ouverts, et aucun prélèvement ne part.
    </p>
  ),

  MODE_PRODUCTION: (
    <>
      <p>
        Tant que ce réglage est sur « Non », votre espace est une démonstration : tous les modules
        sont ouverts et garnis d'exemples, pour que vous puissiez tout parcourir sans rien casser.
      </p>
      <p>
        Passer sur « Oui » <strong>efface les données d'exemple</strong> — factures, dons et
        réservations fictifs — pour que votre première année réelle parte d'une caisse vide. Vos
        réglages et ce que vous avez créé vous-même sont conservés. Ce passage ne se refait pas.
      </p>
    </>
  ),

  SITE_URL: (
    <p>
      L'adresse publique de votre espace. Elle sert à fabriquer les liens des courriers que vous
      envoyez : confirmation d'inscription, page de règlement, invitation à un événement.
      Renseignée de travers, les liens des mails ne mènent nulle part.
    </p>
  ),

  MAILER_DSN: (
    <>
      <p>
        L'adresse technique du serveur qui expédie vos courriers, fournie par votre hébergeur. Elle
        rassemble en une ligne le serveur, l'identifiant et le mot de passe de la boîte d'envoi.
      </p>
      <p>
        <strong>Ce champ est rempli à l'installation et ne se modifie qu'avec l'assistance.</strong>{' '}
        Une erreur ici arrête tous vos courriers d'un coup, sans message d'alerte.
      </p>
    </>
  ),

  NOM_EXPEDITEUR: (
    <p>
      Le nom qui s'affiche dans la boîte de réception de vos destinataires, avant même l'objet du
      message. « Comité des fêtes » se reconnaît ; une adresse technique se supprime sans être
      ouverte.
    </p>
  ),

  MODES_PAIEMENT_ACTIFS: (
    <p>
      Les moyens de règlement proposés à vos adhérents : espèces, chèque, virement, carte bancaire.
      Ce que vous décochez disparaît des pages de paiement et des relances. La carte bancaire
      suppose que vos clés d'encaissement soient renseignées.
    </p>
  ),

  PAIEMENT_TITULAIRE: (
    <p>
      Le nom exact du titulaire du compte, tel qu'il figure sur votre relevé bancaire. Il paraît sur
      les factures réglées par virement : une banque peut rejeter un virement dont le libellé ne
      correspond pas à l'IBAN.
    </p>
  ),

  STRIPE_MODE_TEST: (
    <>
      <p>
        En mode test, les règlements se simulent : les cartes d'essai passent, aucun euro ne bouge,
        et rien n'apparaît sur votre compte bancaire. C'est le mode où l'on vérifie que la chaîne
        fonctionne de bout en bout.
      </p>
      <p>
        <strong>Tant qu'il est actif, aucun règlement réel n'est encaissé.</strong> C'est l'oubli le
        plus fréquent d'une mise en service : pensez à le repasser sur « Non » le jour de
        l'ouverture.
      </p>
    </>
  ),

  BOUTIQUE_FRAIS_GRILLE: (
    <>
      <p>
        Le barème d'expédition, par tranches. Tout article vendu est expédié — il n'y a pas de
        retrait au local — et chacun compte pour un nombre de parts que vous fixez sur sa fiche : un
        porte-clés en vaut une, un survêtement plusieurs.
      </p>
      <p>
        Le panier additionne les parts et retient la tranche correspondante. La dernière tranche,
        sans borne haute, s'applique à tout ce qui dépasse.
      </p>
      <p>
        Le barème s'écrit sous la forme{' '}
        <code>[&#123;"jusqua":1,"prix":5&#125;,&#123;"jusqua":3,"prix":8&#125;,&#123;"jusqua":null,"prix":12&#125;]</code>{' '}
        — soit : jusqu'à une part, 5 € ; jusqu'à trois, 8 € ; au-delà, 12 €.{' '}
        <strong>Le dernier palier doit porter <code>null</code></strong>, sans quoi un panier plus
        gros que prévu partirait sans frais de port.
      </p>
    </>
  ),

  TVA_BOUTIQUE: (
    <p>
      Le taux appliqué aux ventes de la boutique. La plupart des associations ne sont pas assujetties
      à la TVA : dans ce cas, laissez 0 — vos factures porteront alors la mention d'exonération
      prévue par le code général des impôts.
    </p>
  ),

  TVA_SPONSOR: (
    <p>
      Le taux appliqué aux factures de sponsoring. Une association non assujettie laisse 0. Le
      sponsoring est une prestation publicitaire, à distinguer du mécénat, qui ouvre droit à un reçu
      fiscal et non à une facture.
    </p>
  ),

  SPONSORING_PAR_EVENEMENT: (
    <p>
      Rattache chaque contrat de sponsoring à un événement précis plutôt qu'à la saison entière. À
      retenir si vos partenaires soutiennent un tournoi ou une fête en particulier, et non le club
      dans l'année.
    </p>
  ),

  FACTURATION_RAISON_SOCIALE: (
    <p>
      Le nom officiel de l'association, celui déposé en préfecture — pas son nom d'usage ni son
      abréviation. C'est lui qui figure en tête de vos factures, et c'est à lui que vos sponsors
      établiront leur règlement.
    </p>
  ),

  FACTURATION_RNA: (
    <p>
      Le numéro d'inscription au répertoire national des associations, de la forme W suivi de neuf
      chiffres. Il figure sur votre récépissé de déclaration en préfecture. Toute association
      déclarée en a un, même sans SIRET.
    </p>
  ),

  FACTURATION_SIRET: (
    <>
      <p>
        Les quatorze chiffres qui identifient votre établissement. Une association n'en a que si elle
        a demandé son immatriculation — pour employer, encaisser des subventions publiques ou
        facturer.
      </p>
      <p>
        Si vous n'en avez pas, laissez vide : <strong>mieux vaut une mention absente qu'un numéro
        inventé</strong>, qu'un sponsor ne retrouvera dans aucun registre.
      </p>
    </>
  ),

  FACTURATION_REGIME_TVA: (
    <p>
      Votre situation au regard de la TVA. La grande majorité des associations relèvent de la
      franchise en base ou de l'exonération de l'article 261-7 : elles ne facturent pas de TVA et ne
      la récupèrent pas. Le régime choisi commande la mention imprimée en pied de facture.
    </p>
  ),

  FACTURATION_DELAI_PAIEMENT_JOURS: (
    <p>
      Le délai accordé à vos clients pour régler, compté depuis l'émission de la facture. Trente
      jours est l'usage. C'est aussi la date à partir de laquelle les pénalités de retard courent, et
      à laquelle les relances se déclenchent.
    </p>
  ),

  FACTURATION_TAUX_PENALITES: (
    <>
      <p>
        Le taux annuel appliqué aux factures réglées en retard. Sa mention sur la facture est
        obligatoire, même si vous n'entendez jamais l'appliquer.
      </p>
      <p>
        Le minimum légal est de trois fois le taux d'intérêt légal. En pratique, le taux de la BCE
        majoré de dix points est ce que retiennent la plupart des associations.
      </p>
    </>
  ),

  FACTURATION_VALIDITE_DEVIS_JOURS: (
    <p>
      Combien de temps votre devis engage vos prix. Passé ce délai, vous n'êtes plus tenu par le
      montant proposé — utile quand un partenaire ressort un devis d'il y a deux ans.
    </p>
  ),

  ASSOCIATION_OBJET: (
    <p>
      L'objet de l'association tel qu'il est écrit dans vos statuts, mot pour mot. Il paraît sur vos
      mentions légales et sur votre page de présentation. Le recopier plutôt que le reformuler évite
      d'avoir deux versions qui divergent.
    </p>
  ),

  ASSOCIATION_JO_PARUTION: (
    <p>
      La date de parution de votre déclaration au Journal officiel des associations, telle qu'elle
      figure sur votre annonce. C'est cette parution qui donne à l'association sa personnalité
      juridique — elle et son numéro d'annonce sont attendus dans les mentions légales.
    </p>
  ),

  MENTIONS_LEGALES_DIRECTEUR_PUBLICATION: (
    <p>
      La personne responsable de ce qui est publié sur le site : par défaut le président de
      l'association. Son nom doit figurer sur un site accessible au public. À revoir après chaque
      assemblée générale qui change le bureau.
    </p>
  ),

  MENTIONS_LEGALES_HEBERGEUR: (
    <p>
      Le nom et l'adresse de la société qui héberge le site. La loi impose de les afficher pour que
      chacun sache à qui s'adresser en cas de litige. Ce champ est renseigné à l'installation et
      n'appelle pas de modification.
    </p>
  ),

  RGPD_DPO_EMAIL: (
    <p>
      L'adresse à laquelle vos adhérents écrivent pour consulter, corriger ou faire supprimer leurs
      données. Une association n'est pas tenue de désigner un délégué à la protection des données,
      mais elle doit offrir un point de contact : à défaut, indiquez l'adresse du bureau.
    </p>
  ),

  TELEGRAM_BOT_TOKEN: (
    <>
      <p>
        La clé du robot que vous avez créé auprès de BotFather, sur Telegram. Elle lui permet de
        publier dans vos canaux.
      </p>
      <p>
        <strong>Elle vaut mot de passe</strong> : qui la détient peut écrire au nom de
        l'association. Elle ne se partage pas hors du bureau, et se régénère auprès de BotFather si
        elle a circulé.
      </p>
    </>
  ),

  TELEGRAM_CANAL_ADMIN: (
    <p>
      Le canal réservé au bureau. Il reçoit ce qui demande une décision ou touche à l'argent : un
      visuel à valider, un nouveau sponsor et son montant, une vente de la boutique. N'y invitez que
      les membres du bureau.
    </p>
  ),

  TELEGRAM_CANAL_ASSOCIATION: (
    <p>
      Le canal ouvert aux adhérents. Il reçoit ce qui se partage : l'arrivée d'un membre ou d'un
      bénévole, un nouveau partenaire — <strong>sans le montant de son engagement</strong>, qui
      reste au bureau.
    </p>
  ),

  THEME_COULEUR_PRIMAIRE: (
    <p>
      La couleur dominante de votre espace et de votre site public : boutons, liens, en-têtes.
      Reprenez celle de votre logo. Une teinte très claire passe mal sur fond blanc — le texte posé
      dessus deviendrait illisible.
    </p>
  ),

  DEMO_IDENTIFIANTS: (
    <p>
      Les identifiants affichés sur la page de connexion d'un espace de démonstration, pour qu'un
      visiteur puisse entrer et regarder. Videz ce champ dès la mise en production : les laisser
      revient à publier une clé.
    </p>
  ),
};

/* ------------------ Les blocs de la gestion des adhérents --------------- */

export const AIDES_ADHERENTS: Record<string, React.ReactNode> = {
  'Frais bancaires': (
    <>
      <p>
        Chaque règlement par carte est amputé d'une commission de{' '}
        <strong>1,5 % + 0,25 €</strong> avant d'arriver sur votre compte. Une licence à 120 € vous
        en rapporte donc 117,95 € — soit, sur cent-cinquante licenciés, plus de trois cents euros
        qui manquent au budget sans que rien ne l'indique nulle part.
      </p>
      <p>
        En cochant la case, la famille règle <strong>122,09 €</strong> pour une licence à 120 € :
        la commission apparaît sur son paiement sous une ligne nommée « Frais bancaires », et votre
        association reçoit bien ses 120 €.
      </p>
      <p>
        La cotisation reste enregistrée à 120 € dans votre registre : les frais ne comptent pas
        comme un règlement, et vos comptes ne s'en trouvent pas gonflés. Rien ne change pour les
        paiements encaissés à la main, ni pour le prélèvement en plusieurs fois.
      </p>
    </>
  ),

  Calendrier: (
    <>
      <p>
        Les rencontres de la saison : date, adversaire, domicile ou déplacement, lieu, compétition
        et résultat. Vos licenciés les retrouvent dans leur espace, avec leur place dans le groupe.
      </p>
      <p>
        <strong>Convoqué</strong> et <strong>présent</strong> restent deux choses distinctes : vous
        convoquez le jeudi, deux joueurs manquent le dimanche, et l'écart se voit. C'est
        précisément l'information qui manque quand on efface les absents pour que la feuille de
        match soit juste.
      </p>
      <p>
        Saisir un score bascule la rencontre en « jouée ». Une rencontre à venir n'a pas de score,
        et n'est pas comptée comme un match nul.
      </p>
    </>
  ),

  'Numéro d’affiliation': (
    <>
      <p>
        Le numéro sous lequel votre fédération vous connaît. C'est lui qui part sur vos feuilles de
        match, vos demandes de subvention et vos courriers à la ligue.
      </p>
      <p>
        Il est <strong>par fédération</strong> : un club omnisports en a un au district de
        football, un autre à la fédération de gymnastique. Vous le retrouvez dans vos courriers
        types sous le repère{' '}
        <code>{'{{ discipline.immatriculation }}'}</code>.
      </p>
    </>
  ),

  'Courriers types': (
    <>
      <p>
        Les messages que vous envoyez souvent — accueil d'un nouveau licencié, rappel de
        cotisation, convocation. Les repères entre doubles accolades sont remplacés au moment où le
        courrier est produit.
      </p>
      <p>
        Ils connaissent votre club, <strong>votre discipline et sa fédération</strong>, la saison,
        l'adhérent et sa licence. La liste complète, avec un exemple de chaque, se déplie sous les
        courriers.
      </p>
      <p>
        Vous les retrouvez remplis depuis la fiche d'une licence, prêts à copier dans votre
        messagerie.
      </p>
    </>
  ),

  Saisons: (
    <>
      <p>
        Les saisons de votre club. Une licence, une cotisation et une équipe appartiennent toujours
        à une saison : c'est ce qui permet de comparer une année à l'autre et de repartir à zéro
        sans rien perdre.
      </p>
      <p>
        Une seule est <strong>ouverte</strong> à la fois — celle où s'inscrivent les nouveaux
        adhérents. Les précédentes restent consultables.
      </p>
    </>
  ),

  Disciplines: (
    <>
      <p>
        Les sports ou activités que vous proposez. Chaque licence en porte une, et les tarifs de
        cotisation se règlent par discipline et par catégorie.
      </p>
      <p>
        Pour gérer plusieurs sports <strong>en parallèle sur la même saison</strong> — chacun avec
        sa fédération, ses catégories d'âge et son numéro d'affiliation — souscrivez le module
        <strong> Association omnisports</strong>. Sans lui, choisir une nouvelle discipline
        remplace la précédente.
      </p>
    </>
  ),

  "Catégories d'âge": (
    <>
      <p>
        Les catégories de votre fédération — U9, U11, seniors, vétérans — avec l'âge minimum et
        maximum de chacune. La catégorie d'un adhérent se calcule seule à partir de sa date de
        naissance.
      </p>
      <p>
        Un entraîneur peut ensuite <strong>surclasser</strong> un joueur qui évolue avec les plus
        grands, ou le déclasser : le calcul propose, il n'impose pas.
      </p>
    </>
  ),

  'Types de licence': (
    <p>
      Compétition, loisir, dirigeant, arbitre… Ce qui distingue les licences d'un même club. Le
      type figure sur la licence et sert à filtrer le registre — utile quand la fédération réclame
      le nombre de licenciés d'une catégorie précise.
    </p>
  ),

  'Pièces réclamées': (
    <>
      <p>
        Les documents que vous demandez à l'inscription : certificat médical, photo d'identité,
        autorisation parentale. Chacun peut être <strong>obligatoire</strong> — le dossier reste
        alors incomplet tant qu'il manque — et porter une durée de validité.
      </p>
      <p>
        Un certificat valable douze mois déclenche une relance à son échéance, sans que personne
        n'ait à surveiller un tableau.
      </p>
    </>
  ),

  'Précisions propres au sport': (
    <p>
      Ce que votre discipline demande et qu'aucune autre ne demande : un poste au handball, une
      catégorie de poids en judo, une main directrice au tennis. Chaque précision devient un champ
      sur la fiche de l'adhérent, en texte libre ou en liste fermée.
    </p>
  ),

  Équipements: (
    <>
      <p>
        Ce que le club prête ou vend : maillots, survêtements, sacs. Chaque équipement a ses
        tailles, et l'inventaire retient qui détient quoi.
      </p>
      <p>
        C'est ce qui permet, en fin de saison, de savoir <strong>quels maillots ne sont pas
        rentrés</strong> — et chez qui.
      </p>
    </>
  ),

  'Rôles dans une équipe': (
    <p>
      Les rôles que peut tenir un adhérent dans une équipe : joueur, capitaine, gardien,
      remplaçant, entraîneur, arbitre. Ils paraissent sur la feuille de match, où les remplaçants
      sont listés à part.
    </p>
  ),

  'Vitrine publique': (
    <>
      <p>
        Ce que les visiteurs de votre site voient de vos équipes : leur composition, les noms, et
        éventuellement les photos.
      </p>
      <p>
        <strong>Rien n'est publié sans que vous l'ayez demandé.</strong> Les photos des mineurs ont
        leur propre case, distincte de celle des majeurs : publier le visage d'un adulte qui joue en
        senior est une chose, celui d'un enfant de onze ans en est une autre, et cela demande
        l'accord écrit de ses responsables légaux.
      </p>
    </>
  ),

  "Relances d'échéance": (
    <>
      <p>
        Les rappels envoyés avant qu'une cotisation ou une pièce n'arrive à échéance, en jours :
        trente jours avant, sept jours avant, le jour même.
      </p>
      <p>
        Ils partent à qui paie — l'adhérent s'il est majeur, ses responsables légaux sinon.
        Rappeler sa cotisation à un enfant de neuf ans n'a jamais fait rentrer un euro.
      </p>
    </>
  ),

  'Paiement en plusieurs fois': (
    <>
      <p>
        Autorise les familles à étaler une cotisation, et décide jusqu'où : en trois fois, en cinq,
        ou tous les mois. Le règlement comptant reste toujours proposé.
      </p>
      <p>
        La famille signe une autorisation de prélèvement en ligne, après avoir lu son échéancier —
        chaque montant, à chaque date. <strong>Une échéance n'est comptée réglée qu'au retour de la
        banque</strong> : un prélèvement met plusieurs jours à être confirmé et peut être rejeté
        après coup.
      </p>
    </>
  ),
};
